import { useState, useEffect, useCallback } from 'react'
import { apiManagementService } from '../services/apiManagementService'

const AVAILABLE_PERMISSIONS = [
  { value: 'all', label: 'Full Access', desc: 'Akses semua endpoint' },
  { value: 'stock.read', label: 'Baca Stok', desc: 'GET stok & movements' },
  { value: 'stock.reserve', label: 'Reserve Stok', desc: 'Reserve, release, confirm stok' },
  { value: 'products.read', label: 'Baca Produk', desc: 'GET daftar buku' },
  { value: 'schools.read', label: 'Baca Sekolah', desc: 'GET daftar sekolah' },
  { value: 'spk.read', label: 'Baca SPK', desc: 'GET daftar & detail SPK' },
  { value: 'webhooks.manage', label: 'Kelola Webhook', desc: 'CRUD webhook subscriptions' },
]

function ApiManagementPage() {
  const [stats, setStats] = useState(null)
  const [clients, setClients] = useState([])
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('clients')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showKeyModal, setShowKeyModal] = useState(null) // { id, api_key }
  const [editClient, setEditClient] = useState(null)
  const [logFilter, setLogFilter] = useState({ client_id: '', page: 1 })

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      const [statsRes, clientsRes] = await Promise.all([
        apiManagementService.getStats(),
        apiManagementService.getClients(),
      ])
      setStats(statsRes.data)
      setClients(clientsRes.data)
    } catch (err) {
      console.error('Failed to fetch API data:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchLogs = useCallback(async () => {
    try {
      const res = await apiManagementService.getLogs(logFilter)
      setLogs(res.data)
    } catch (err) {
      console.error('Failed to fetch logs:', err)
    }
  }, [logFilter])

  useEffect(() => { fetchData() }, [fetchData])
  useEffect(() => { if (activeTab === 'logs') fetchLogs() }, [activeTab, fetchLogs])

  const handleDelete = async (id, name) => {
    if (!confirm(`Hapus API client "${name}"? Semua webhook & reservasi terkait akan ikut terhapus.`)) return
    try {
      await apiManagementService.deleteClient(id)
      fetchData()
    } catch (err) {
      alert('Gagal menghapus: ' + (err.response?.data?.message || err.message))
    }
  }

  const handleRegenerateKey = async (id, name) => {
    if (!confirm(`Regenerate API key untuk "${name}"? Key lama akan tidak berlaku.`)) return
    try {
      const res = await apiManagementService.regenerateKey(id)
      setShowKeyModal(res.data)
    } catch (err) {
      alert('Gagal regenerate: ' + (err.response?.data?.message || err.message))
    }
  }

  const handleToggleStatus = async (client) => {
    try {
      await apiManagementService.updateClient(client.id, {
        status: client.status === 'active' ? 'inactive' : 'active'
      })
      fetchData()
    } catch (err) {
      alert('Gagal update status: ' + (err.response?.data?.message || err.message))
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-10 h-10"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">API Management</h1>
          <p className="text-sm text-gray-500">Kelola API key, webhook, dan monitor penggunaan API eksternal</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
        >
          + Tambah Client
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard label="Client Aktif" value={stats.active_clients} color="blue" />
          <StatCard label="Webhook Aktif" value={stats.active_webhooks} color="purple" />
          <StatCard label="Request (24j)" value={stats.requests_24h} color="green" />
          <StatCard label="Reservasi Aktif" value={stats.active_reservations} color="orange" />
          <StatCard label="Qty Reserved" value={stats.reserved_qty} color="red" />
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-6">
          {[
            { key: 'clients', label: 'API Clients' },
            { key: 'logs', label: 'Request Logs' },
            { key: 'docs', label: 'Dokumentasi API' },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition ${
                activeTab === tab.key
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'clients' && (
        <ClientsTab
          clients={clients}
          onDelete={handleDelete}
          onRegenerate={handleRegenerateKey}
          onToggleStatus={handleToggleStatus}
          onEdit={setEditClient}
        />
      )}

      {activeTab === 'logs' && (
        <LogsTab
          logs={logs}
          clients={clients}
          filter={logFilter}
          onFilterChange={setLogFilter}
        />
      )}

      {activeTab === 'docs' && <DocsTab />}

      {/* Create/Edit Modal */}
      {(showCreateModal || editClient) && (
        <ClientModal
          client={editClient}
          onClose={() => { setShowCreateModal(false); setEditClient(null) }}
          onSave={async (data) => {
            try {
              if (editClient) {
                await apiManagementService.updateClient(editClient.id, data)
              } else {
                const res = await apiManagementService.createClient(data)
                setShowKeyModal(res.data)
              }
              setShowCreateModal(false)
              setEditClient(null)
              fetchData()
            } catch (err) {
              alert('Gagal: ' + (err.response?.data?.message || err.message))
            }
          }}
        />
      )}

      {/* API Key Display Modal */}
      {showKeyModal && (
        <KeyModal data={showKeyModal} onClose={() => setShowKeyModal(null)} />
      )}
    </div>
  )
}

// ============================================
// SUB COMPONENTS
// ============================================

function StatCard({ label, value, color }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    orange: 'bg-orange-50 text-orange-700 border-orange-200',
    red: 'bg-red-50 text-red-700 border-red-200',
  }
  return (
    <div className={`rounded-lg border p-4 ${colors[color]}`}>
      <p className="text-sm opacity-75">{label}</p>
      <p className="text-2xl font-bold">{value?.toLocaleString()}</p>
    </div>
  )
}

function ClientsTab({ clients, onDelete, onRegenerate, onToggleStatus, onEdit }) {
  if (clients.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p className="text-4xl mb-2">🔑</p>
        <p>Belum ada API client. Klik "Tambah Client" untuk membuat.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {clients.map(client => (
        <div key={client.id} className="bg-white rounded-lg border p-5">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h3 className="font-semibold text-lg">{client.name}</h3>
                <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                  client.status === 'active'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-500'
                }`}>
                  {client.status}
                </span>
              </div>
              {client.description && (
                <p className="text-sm text-gray-500 mt-1">{client.description}</p>
              )}
              <div className="flex flex-wrap gap-1.5 mt-2">
                {(client.permissions || []).map(p => (
                  <span key={p} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded">
                    {p}
                  </span>
                ))}
              </div>
              <div className="flex gap-6 mt-3 text-xs text-gray-400">
                <span>Rate limit: {client.rate_limit}/min</span>
                <span>Request 24j: {client.requests_24h || 0}</span>
                <span>Webhook: {client.webhook_count || 0}</span>
                <span>Reservasi: {client.active_reservations || 0}</span>
                {client.last_used_at && (
                  <span>Terakhir: {new Date(client.last_used_at).toLocaleString('id-ID')}</span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 ml-4">
              <button
                onClick={() => onToggleStatus(client)}
                className={`px-3 py-1.5 text-xs rounded-lg transition ${
                  client.status === 'active'
                    ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                }`}
              >
                {client.status === 'active' ? 'Nonaktifkan' : 'Aktifkan'}
              </button>
              <button
                onClick={() => onEdit(client)}
                className="px-3 py-1.5 text-xs bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition"
              >
                Edit
              </button>
              <button
                onClick={() => onRegenerate(client.id, client.name)}
                className="px-3 py-1.5 text-xs bg-yellow-50 text-yellow-700 rounded-lg hover:bg-yellow-100 transition"
              >
                Regenerate Key
              </button>
              <button
                onClick={() => onDelete(client.id, client.name)}
                className="px-3 py-1.5 text-xs bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition"
              >
                Hapus
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

function LogsTab({ logs, clients, filter, onFilterChange }) {
  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        <select
          value={filter.client_id}
          onChange={e => onFilterChange({ ...filter, client_id: e.target.value, page: 1 })}
          className="px-3 py-2 border rounded-lg text-sm"
        >
          <option value="">Semua Client</option>
          {clients.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      <div className="bg-white rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-600">Waktu</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">Client</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">Method</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">Path</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">Status</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">Time</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">IP</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {logs.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-4 py-8 text-center text-gray-400">Belum ada log</td>
              </tr>
            ) : logs.map(log => (
              <tr key={log.id} className="hover:bg-gray-50">
                <td className="px-4 py-2 text-gray-500 whitespace-nowrap">
                  {new Date(log.created_at).toLocaleString('id-ID')}
                </td>
                <td className="px-4 py-2">{log.client_name || '-'}</td>
                <td className="px-4 py-2">
                  <span className={`px-1.5 py-0.5 text-xs rounded font-mono ${
                    log.method === 'GET' ? 'bg-green-100 text-green-700' :
                    log.method === 'POST' ? 'bg-blue-100 text-blue-700' :
                    log.method === 'DELETE' ? 'bg-red-100 text-red-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {log.method}
                  </span>
                </td>
                <td className="px-4 py-2 font-mono text-xs">{log.path}</td>
                <td className="px-4 py-2">
                  <span className={`text-xs font-medium ${
                    log.status_code < 300 ? 'text-green-600' :
                    log.status_code < 400 ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    {log.status_code}
                  </span>
                </td>
                <td className="px-4 py-2 text-gray-500">{log.response_time}ms</td>
                <td className="px-4 py-2 text-gray-400 font-mono text-xs">{log.ip_address}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function DocsTab() {
  return (
    <div className="bg-white rounded-lg border p-6 space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">Autentikasi</h3>
        <p className="text-sm text-gray-600 mb-2">Semua request ke API v1 memerlukan header <code className="bg-gray-100 px-1.5 py-0.5 rounded">X-API-Key</code>.</p>
        <pre className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm overflow-x-auto">
{`curl -H "X-API-Key: YOUR_API_KEY" \\
  https://your-domain.com/api/v1/stock`}
        </pre>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-2">Endpoint</h3>
        <table className="w-full text-sm border">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left border-b">Method</th>
              <th className="px-3 py-2 text-left border-b">Path</th>
              <th className="px-3 py-2 text-left border-b">Permission</th>
              <th className="px-3 py-2 text-left border-b">Deskripsi</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {[
              ['GET', '/api/v1/stock', 'stock.read', 'Daftar stok gudang'],
              ['GET', '/api/v1/stock/summary', 'stock.read', 'Ringkasan stok per buku'],
              ['GET', '/api/v1/stock/movements', 'stock.read', 'Riwayat pergerakan stok'],
              ['GET', '/api/v1/stock/:id', 'stock.read', 'Detail stok entry'],
              ['POST', '/api/v1/stock/reserve', 'stock.reserve', 'Reserve stok'],
              ['POST', '/api/v1/stock/release', 'stock.reserve', 'Batalkan reservasi'],
              ['POST', '/api/v1/stock/confirm', 'stock.reserve', 'Konfirmasi reservasi (stok keluar)'],
              ['GET', '/api/v1/stock/reservations', 'stock.reserve', 'Daftar reservasi saya'],
              ['GET', '/api/v1/products', 'products.read', 'Daftar buku'],
              ['GET', '/api/v1/products/:id', 'products.read', 'Detail buku + stok'],
              ['GET', '/api/v1/schools', 'schools.read', 'Daftar sekolah'],
              ['GET', '/api/v1/spk', 'spk.read', 'Daftar SPK'],
              ['GET', '/api/v1/spk/:id', 'spk.read', 'Detail SPK'],
              ['GET', '/api/v1/webhooks', 'webhooks.manage', 'Daftar webhook'],
              ['POST', '/api/v1/webhooks', 'webhooks.manage', 'Buat webhook baru'],
              ['DELETE', '/api/v1/webhooks/:id', 'webhooks.manage', 'Hapus webhook'],
            ].map(([method, path, perm, desc], i) => (
              <tr key={i} className="hover:bg-gray-50">
                <td className="px-3 py-1.5">
                  <span className={`px-1.5 py-0.5 text-xs rounded font-mono ${
                    method === 'GET' ? 'bg-green-100 text-green-700' :
                    method === 'POST' ? 'bg-blue-100 text-blue-700' :
                    'bg-red-100 text-red-700'
                  }`}>{method}</span>
                </td>
                <td className="px-3 py-1.5 font-mono text-xs">{path}</td>
                <td className="px-3 py-1.5"><span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 text-xs rounded">{perm}</span></td>
                <td className="px-3 py-1.5 text-gray-600">{desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-2">Webhook Events</h3>
        <div className="grid grid-cols-2 gap-2 text-sm">
          {[
            ['stock.updated', 'Stok berubah (masuk gudang)'],
            ['stock.reserved', 'Stok di-reserve oleh client'],
            ['stock.released', 'Reservasi dibatalkan'],
            ['stock.confirmed', 'Reservasi dikonfirmasi (stok keluar)'],
            ['spk.completed', 'SPK selesai produksi'],
            ['warehouse.entry', 'Barang masuk gudang'],
            ['*', 'Semua event'],
          ].map(([event, desc]) => (
            <div key={event} className="flex gap-2 p-2 bg-gray-50 rounded">
              <code className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded whitespace-nowrap">{event}</code>
              <span className="text-gray-600">{desc}</span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-2">Rate Limiting</h3>
        <p className="text-sm text-gray-600">
          Setiap client memiliki rate limit per menit. Response headers:
        </p>
        <ul className="text-sm text-gray-600 list-disc list-inside mt-1 space-y-1">
          <li><code className="bg-gray-100 px-1 rounded">X-RateLimit-Limit</code> — Batas request per menit</li>
          <li><code className="bg-gray-100 px-1 rounded">X-RateLimit-Remaining</code> — Sisa request</li>
          <li><code className="bg-gray-100 px-1 rounded">X-RateLimit-Reset</code> — Waktu reset (Unix timestamp)</li>
        </ul>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-2">Verifikasi Webhook Signature</h3>
        <pre className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm overflow-x-auto">
{`// Node.js example
const crypto = require('crypto');

function verifyWebhook(body, signature, secret) {
  const expected = 'sha256=' +
    crypto.createHmac('sha256', secret)
      .update(JSON.stringify(body))
      .digest('hex');
  return signature === expected;
}

// In your webhook handler:
app.post('/webhook', (req, res) => {
  const sig = req.headers['x-webhook-signature'];
  if (!verifyWebhook(req.body, sig, YOUR_WEBHOOK_SECRET)) {
    return res.status(401).send('Invalid signature');
  }
  // Process event...
  console.log(req.body.event, req.body.data);
  res.sendStatus(200);
});`}
        </pre>
      </div>
    </div>
  )
}

function ClientModal({ client, onClose, onSave }) {
  const [form, setForm] = useState({
    name: client?.name || '',
    description: client?.description || '',
    permissions: client?.permissions || ['stock.read', 'products.read'],
    rate_limit: client?.rate_limit || 100,
  })
  const [saving, setSaving] = useState(false)

  const togglePerm = (perm) => {
    setForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(perm)
        ? prev.permissions.filter(p => p !== perm)
        : [...prev.permissions, perm]
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.name.trim()) return alert('Nama wajib diisi')
    setSaving(true)
    try {
      await onSave(form)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
        <h2 className="text-xl font-bold mb-4">{client ? 'Edit API Client' : 'Tambah API Client'}</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nama Aplikasi *</label>
            <input
              type="text"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="contoh: SIM Penjualan"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Deskripsi</label>
            <input
              type="text"
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Deskripsi singkat aplikasi"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Permissions</label>
            <div className="space-y-2">
              {AVAILABLE_PERMISSIONS.map(p => (
                <label key={p.value} className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.permissions.includes(p.value)}
                    onChange={() => togglePerm(p.value)}
                    className="mt-0.5"
                  />
                  <div>
                    <span className="text-sm font-medium">{p.label}</span>
                    <span className="text-xs text-gray-400 ml-2">{p.value}</span>
                    <p className="text-xs text-gray-500">{p.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rate Limit (per menit)</label>
            <input
              type="number"
              value={form.rate_limit}
              onChange={e => setForm({ ...form, rate_limit: parseInt(e.target.value) || 100 })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              min="10"
              max="10000"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50 transition"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
            >
              {saving ? 'Menyimpan...' : client ? 'Update' : 'Buat Client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function KeyModal({ data, onClose }) {
  const [copied, setCopied] = useState(false)

  const copyKey = () => {
    navigator.clipboard.writeText(data.api_key)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full max-w-lg mx-4 p-6">
        <div className="text-center mb-4">
          <span className="text-4xl">🔑</span>
          <h2 className="text-xl font-bold mt-2">API Key</h2>
          <p className="text-sm text-red-500 mt-1">Simpan key ini sekarang! Tidak bisa dilihat lagi nanti.</p>
        </div>

        <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm break-all">
          {data.api_key}
        </div>

        <div className="flex gap-3 mt-4">
          <button
            onClick={copyKey}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            {copied ? 'Tersalin!' : 'Salin Key'}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded-lg hover:bg-gray-50 transition"
          >
            Tutup
          </button>
        </div>
      </div>
    </div>
  )
}

export default ApiManagementPage
